import { useMemo, useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertCircle, ArrowLeft, Loader2, Plus, RefreshCcw, Search, Trash2 } from 'lucide-react';
import { api, LitLegionTaskStatusResponse } from '@/lib/api';
import { useAdminUserTaskStatuses } from '../hooks/useAdminUserTaskStatuses';
import { useAdminTasks } from '../hooks/useAdminTasks';
import { LL_TASK_STATUSES, isLLTaskStatus } from '../types';

export function AdminUserTaskStatusesPage() {
  const {
    statuses,
    total,
    loading,
    error,
    loadedUsername,
    resolvedUserId,
    fetchForUsername,
    refresh,
    loadMore,
    hasMore,
    applyLocalUpdate,
    applyLocalDelete,
    applyLocalClear,
    applyLocalAdd,
  } = useAdminUserTaskStatuses();

  const { tasks: allTasks, loading: tasksLoading } = useAdminTasks();

  const [usernameInput, setUsernameInput] = useState('');
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [addTaskId, setAddTaskId] = useState('');
  const [addStatus, setAddStatus] = useState('available');
  const [adding, setAdding] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [toolbarMessage, setToolbarMessage] = useState<string | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteConfirmText, setBulkDeleteConfirmText] = useState('');
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Tasks already on the user are shown grayed out / blocked in the picker.
  // The server enforces the unique (user_id, task_id) constraint, but this
  // saves a round-trip and avoids a confusing 409 message.
  const assignedTaskIds = useMemo(() => new Set(statuses.map((s) => s.taskId)), [statuses]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = usernameInput.trim();
    if (!trimmed) return;
    setActionError(null);
    fetchForUsername(trimmed);
  };

  const handleStatusChange = async (taskStatus: LitLegionTaskStatusResponse, newStatus: string) => {
    if (!isLLTaskStatus(newStatus) || newStatus === taskStatus.status) return;

    // Admin override bypasses the player state machine: setting status to
    // `awarded` does NOT grant XP, coin rewards, or badge rewards (those only
    // fire from the player flow). Make sure the admin understands that
    // before we let them put the player into a state that looks earned.
    if (newStatus === 'awarded' || newStatus === 'completed') {
      const proceed = window.confirm(
        `Setting status to "${newStatus}" via admin override does NOT grant XP, ` +
          `coin rewards, or badge rewards. The player will see the task as ` +
          `${newStatus} but will not receive any rewards from this change.\n\n` +
          `Continue?`,
      );
      if (!proceed) return;
    }

    setUpdatingId(taskStatus.taskStatusId);
    setActionError(null);
    try {
      const result = await api.adminUpdateLitLegionTaskStatus(taskStatus.taskStatusId, newStatus);
      applyLocalUpdate(result.taskStatus);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to update task status');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleUnpin = async (taskStatus: LitLegionTaskStatusResponse) => {
    setUpdatingId(taskStatus.taskStatusId);
    setActionError(null);
    try {
      const result = await api.adminUpdateLitLegionTaskStatus(
        taskStatus.taskStatusId,
        taskStatus.storedStatus ?? taskStatus.status,
        false,
      );
      applyLocalUpdate(result.taskStatus);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to unpin task status');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (taskStatus: LitLegionTaskStatusResponse) => {
    const confirmed = window.confirm(
      `Delete task status ${taskStatus.taskStatusId}? This cannot be undone. To re-add availability after a delete, use the Recompute availability button.`,
    );
    if (!confirmed) return;

    setDeletingId(taskStatus.taskStatusId);
    setActionError(null);
    try {
      await api.adminDeleteLitLegionTaskStatus(taskStatus.taskStatusId);
      applyLocalDelete(taskStatus.taskStatusId);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete task status');
    } finally {
      setDeletingId(null);
    }
  };

  const handleRecompute = async () => {
    if (!resolvedUserId) return;
    setRecomputing(true);
    setActionError(null);
    setToolbarMessage(null);
    try {
      const result = await api.adminRecomputeLitLegionAvailability(resolvedUserId);
      if (result.count === 0) {
        setToolbarMessage(
          'No tasks needed availability — every unblocked task is already assigned.',
        );
      } else {
        setToolbarMessage(
          `Added ${result.count} 'available' task${result.count === 1 ? '' : 's'} for this user.`,
        );
        refresh();
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to recompute availability');
    } finally {
      setRecomputing(false);
    }
  };

  // Case-insensitive: the admin searched `devuser1` and the server echoed back
  // the canonical `DevUser1`. Making them match the stored casing exactly adds
  // friction without adding safety — the point of the gate is deliberate
  // intent, not transcription accuracy.
  const bulkDeleteConfirmed =
    loadedUsername !== null &&
    bulkDeleteConfirmText.trim().toLowerCase() === loadedUsername.toLowerCase();

  const handleBulkDelete = async () => {
    if (!resolvedUserId || !bulkDeleteConfirmed) return;
    setBulkDeleting(true);
    setActionError(null);
    setToolbarMessage(null);
    try {
      const result = await api.adminDeleteAllLitLegionUserTaskStatuses(resolvedUserId);
      applyLocalClear();
      setBulkDeleteOpen(false);
      setBulkDeleteConfirmText('');
      setToolbarMessage(
        `Deleted ${result.deletedCount} task status${result.deletedCount === 1 ? '' : 'es'}. ` +
          `Use Recompute availability to give this user a fresh set of starting tasks.`,
      );
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete task statuses');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleAddStatus = async (e: FormEvent) => {
    e.preventDefault();
    if (!resolvedUserId || !addTaskId || !isLLTaskStatus(addStatus)) return;

    if (assignedTaskIds.has(addTaskId)) {
      setActionError(
        'This task is already assigned to the user. Edit its existing status instead.',
      );
      return;
    }

    setAdding(true);
    setActionError(null);
    try {
      const result = await api.adminAssignLitLegionTaskToUser(resolvedUserId, addTaskId, addStatus);
      applyLocalAdd(result.taskStatus);
      setAddTaskId('');
      setAddStatus('available');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to add task status');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div>
        <Link
          to="/a/arche_lit_legion/admin"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Admin Dashboard
        </Link>
        <h1 className="text-2xl font-semibold">User Task Statuses</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Look up a user's task statuses and override or remove them — one at a time, or all at
          once.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex gap-2">
        <Input
          placeholder="Enter username (e.g. devuser1)"
          value={usernameInput}
          onChange={(e) => setUsernameInput(e.target.value)}
          className="max-w-md"
        />
        <Button type="submit" disabled={loading || !usernameInput.trim()}>
          {loading && !loadedUsername ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Search className="h-4 w-4 mr-2" />
          )}
          Look up
        </Button>
        {loadedUsername && (
          <Button type="button" variant="outline" onClick={refresh} disabled={loading}>
            Refresh
          </Button>
        )}
        {loadedUsername && resolvedUserId && (
          <Button
            type="button"
            variant="outline"
            onClick={handleRecompute}
            disabled={loading || recomputing}
            title="Re-evaluate every task and add 'available' rows for any that are unblocked but missing"
          >
            {recomputing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCcw className="h-4 w-4 mr-2" />
            )}
            Recompute availability
          </Button>
        )}
        {loadedUsername && resolvedUserId && (
          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              setBulkDeleteConfirmText('');
              setActionError(null);
              setBulkDeleteOpen(true);
            }}
            disabled={loading || bulkDeleting || total === 0}
            title="Delete every task status for this user in one go"
          >
            {bulkDeleting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4 mr-2" />
            )}
            Delete all
          </Button>
        )}
      </form>
      {toolbarMessage && <p className="text-sm text-muted-foreground">{toolbarMessage}</p>}

      {error && (
        <Card className="border-destructive">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Suppressed while the bulk-delete dialog is open — that dialog renders
          the error itself, and the overlay would hide this copy anyway. */}
      {actionError && !bulkDeleteOpen && <p className="text-sm text-destructive">{actionError}</p>}

      {loadedUsername && resolvedUserId && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Plus className="h-4 w-4" /> Add task status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAddStatus} className="flex flex-wrap items-end gap-2">
              <div className="flex-1 min-w-[280px] space-y-1">
                <label htmlFor="add-task" className="text-xs text-muted-foreground">
                  Task
                </label>
                <Select value={addTaskId} onValueChange={setAddTaskId}>
                  <SelectTrigger id="add-task">
                    <SelectValue placeholder={tasksLoading ? 'Loading tasks…' : 'Pick a task'} />
                  </SelectTrigger>
                  <SelectContent>
                    {allTasks.map((t) => {
                      const already = assignedTaskIds.has(t.taskId);
                      return (
                        <SelectItem key={t.taskId} value={t.taskId} disabled={already}>
                          {t.name}
                          {already ? ' (already assigned)' : ''}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-[160px] space-y-1">
                <label htmlFor="add-status" className="text-xs text-muted-foreground">
                  Status
                </label>
                <Select value={addStatus} onValueChange={setAddStatus}>
                  <SelectTrigger id="add-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LL_TASK_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" disabled={!addTaskId || adding}>
                {adding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Add
              </Button>
            </form>
            <p className="text-xs text-muted-foreground mt-2">
              Bypasses the player state machine and grants no rewards. Pick `available` to let the
              player start the task; pick `awarded` only when reconciling a previously-completed
              quest.
            </p>
          </CardContent>
        </Card>
      )}

      {loadedUsername && (
        <div>
          <p className="text-sm text-muted-foreground mb-3">
            {total} status{total !== 1 ? 'es' : ''} for{' '}
            <span className="font-medium text-foreground">{loadedUsername}</span>
            {resolvedUserId && <span className="font-mono text-xs ml-2">({resolvedUserId})</span>}
          </p>

          {loading && statuses.length === 0 ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-20 rounded-lg" />
              ))}
            </div>
          ) : statuses.length === 0 ? (
            <p className="text-muted-foreground py-8 text-center">
              No task statuses found for this user.
            </p>
          ) : (
            <div className="space-y-3">
              {statuses.map((s) => (
                <Card key={s.taskStatusId}>
                  <CardContent className="flex items-center justify-between gap-4 py-4">
                    <div className="flex-1 min-w-0 text-sm space-y-1">
                      {s.task ? (
                        <>
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-medium truncate">{s.task.name}</p>
                            {s.task.taskType && (
                              <Badge variant="secondary" className="text-xs shrink-0">
                                {s.task.taskType}
                              </Badge>
                            )}
                            {s.task.category && (
                              <Badge variant="outline" className="text-xs shrink-0">
                                {s.task.category}
                              </Badge>
                            )}
                          </div>
                          {s.task.description && (
                            <p className="text-xs text-muted-foreground line-clamp-2">
                              {s.task.description}
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="font-medium text-muted-foreground italic">(task deleted)</p>
                      )}
                      <div className="flex flex-wrap gap-x-4 text-xs text-muted-foreground">
                        {(s.attempt ?? 1) > 1 && <span>attempt: {s.attempt}</span>}
                        {s.completedAt && (
                          <span>completed: {new Date(s.completedAt).toLocaleString()}</span>
                        )}
                        {s.xpAmountReceived !== null && <span>XP: {s.xpAmountReceived}</span>}
                        <span>updated: {new Date(s.updatedAt).toLocaleString()}</span>
                      </div>
                      {s.userInput && (
                        <p className="text-xs text-muted-foreground">
                          input: <span className="text-foreground">{s.userInput}</span>
                        </p>
                      )}
                      {s.submission && (
                        <p
                          className="text-xs text-muted-foreground truncate"
                          title={JSON.stringify(s.submission, null, 2)}
                        >
                          submission:{' '}
                          <span className="text-foreground font-mono">
                            {JSON.stringify(s.submission)}
                          </span>
                        </p>
                      )}
                      <p className="font-mono text-[10px] text-muted-foreground/70 truncate">
                        {s.taskStatusId} · task {s.taskId}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {s.adminOverride && (
                        <Badge
                          variant="outline"
                          className="text-xs cursor-pointer"
                          title="Pinned by admin override — the availability engine leaves this row alone. Click to unpin."
                          onClick={() => handleUnpin(s)}
                        >
                          pinned ✕
                        </Badge>
                      )}
                      <Select
                        value={s.status}
                        onValueChange={(value) => handleStatusChange(s, value)}
                        disabled={updatingId === s.taskStatusId}
                      >
                        <SelectTrigger
                          className="w-[140px]"
                          aria-label={`Status for ${s.task?.name ?? s.taskId}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {LL_TASK_STATUSES.map((status) => (
                            <SelectItem key={status} value={status}>
                              {status}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        aria-label={`Delete status for ${s.task?.name ?? s.taskId}`}
                        onClick={() => handleDelete(s)}
                        disabled={deletingId === s.taskStatusId}
                      >
                        {deletingId === s.taskStatusId ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {hasMore && (
                <div className="text-center">
                  <Button variant="ghost" onClick={loadMore} disabled={loading}>
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      'Load more'
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete all task statuses for {loadedUsername}?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  This permanently removes the {total} task status
                  {total === 1 ? '' : 'es'} currently listed for{' '}
                  <span className="font-medium text-foreground">{loadedUsername}</span> — including
                  completed and awarded quests and every retired retake attempt. It cannot be
                  undone.
                </p>
                <p>
                  <span className="font-medium text-destructive">
                    This also erases every badge they have earned.
                  </span>{' '}
                  Badge awards hang off these rows and cascade with them, and there is no separate
                  badge inventory to restore from.
                </p>
                <p>
                  XP, level, and coins already minted to their inventory are NOT affected.
                  Afterwards use{' '}
                  <span className="font-medium text-foreground">Recompute availability</span> to
                  hand them a fresh set of starting tasks.
                </p>
                <p>
                  Type <span className="font-mono text-foreground">{loadedUsername}</span> to
                  confirm.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={bulkDeleteConfirmText}
            onChange={(e) => setBulkDeleteConfirmText(e.target.value)}
            placeholder={loadedUsername ?? ''}
            aria-label="Type the username to confirm"
            autoComplete="off"
          />
          {/* The dialog is modal, so an error rendered in the page body would
              sit behind the overlay where the admin cannot see it. */}
          {actionError && <p className="text-sm text-destructive">{actionError}</p>}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                // Keep the dialog mounted while the request is in flight —
                // the default AlertDialogAction closes on click, which would
                // unmount the spinner and hide any error.
                e.preventDefault();
                handleBulkDelete();
              }}
              disabled={bulkDeleting || !bulkDeleteConfirmed}
            >
              {bulkDeleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Delete all
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
